import { type ReactNode } from 'react';
import {
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
} from 'react-aria-components';

import { Description } from '@/Description/Description';
import { ErrorMessage } from '@/ErrorMessage/ErrorMessage';
import { Label } from '@/Label/Label';
import { mergeClassNames } from '@/utils/mergeClassNames';

import styles from './RadioGroup.module.scss';

export type RadioGroupProps = AriaRadioGroupProps & {
  /**
   * Text rendered underneath the `Radio` options to provide additional context
   */
  description?: ReactNode;
  /**
   * An accessible label to associate with the `RadioGroup`. If not provided,
   * `aria-label` or `aria-labelledby` must be provided.
   */
  label?: ReactNode;
  /**
   * A red error message to display below the description if this form element
   * is "invalid".
   */
  errorMessage?: ReactNode;
};

/**
 * `RadioGroup` allows a user to select an item from a list of mutually
 * exclusive options. Built using React aria's
 * [RadioGroup](https://react-spectrum.adobe.com/react-aria/RadioGroup.html)
 * component.
 *
 * Its children must be `Radio` components, each supplied with a unique `value`
 * prop.
 *
 * ```tsx
 * <RadioGroup label="Radio Group Label" defaultValue="option1">
 *   <Radio value="option1">Option 1</Radio>
 *   <Radio value="option2">Option 2</Radio>
 *   <Radio value="option3" isDisabled>Option 3</Radio>
 *   <Radio value="option4">Option 4</Radio>
 * </RadioGroup>
 * ```
 */
export const RadioGroup = ({
  children,
  className,
  isRequired,
  label,
  description,
  errorMessage,
  ...props
}: RadioGroupProps) => {
  return (
    <AriaRadioGroup
      className={(renderProps) =>
        mergeClassNames([styles.radioGroup, className], renderProps)
      }
      data-zds
      isRequired={isRequired}
      {...props}
    >
      {(renderProps) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}
          {typeof children === 'function' ? children(renderProps) : children}
          {description && <Description>{description}</Description>}
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </>
      )}
    </AriaRadioGroup>
  );
};
